"use client";

import React, { useEffect, useCallback, useRef, useId } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import { cn } from "@/lib/utils/cn";
import { Button } from "@/components/forms";
import { Typography } from "./Typography";

export interface DialogProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  subtitle?: string;
  description?: string;
  children: React.ReactNode;
  footer?: React.ReactNode;
  size?: "sm" | "md" | "lg" | "xl" | "2xl" | "full";
  closeOnOverlayClick?: boolean;
  showCloseButton?: boolean;
  variant?: "default" | "danger" | "warning" | "success" | "info";
  className?: string;
  contentClassName?: string;
  dividers?: boolean;
}

const sizeStyles = {
  sm: "max-w-md",
  md: "max-w-lg",
  lg: "max-w-2xl",
  xl: "max-w-4xl",
  "2xl": "max-w-5xl",
  full: "max-w-full mx-4",
};

export function Dialog({
  isOpen,
  onClose,
  title,
  subtitle,
  description,
  children,
  footer,
  size = "md",
  closeOnOverlayClick = true,
  showCloseButton = true,
  variant = "default",
  className,
  contentClassName,
  dividers = true,
}: DialogProps) {
  const id = useId();
  const titleId = `dialog-title-${id}`;
  const descriptionId = `dialog-description-${id}`;
  const dialogRef = useRef<HTMLDivElement>(null);
  const previousActiveElement = useRef<HTMLElement | null>(null);

  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  useEffect(() => {
    if (isOpen) {
      previousActiveElement.current = document.activeElement as HTMLElement;
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
      previousActiveElement.current?.focus();
    }

    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isOpen) {
        handleClose();
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [isOpen, handleClose]);

  // Focus trap
  useEffect(() => {
    if (!isOpen || !dialogRef.current) return;

    const dialog = dialogRef.current;

    const isFocusable = (el: HTMLElement): boolean => {
      if (el.hasAttribute("disabled")) return false;
      if (el.getAttribute("aria-hidden") === "true") return false;
      if (el.hidden) return false;
      // Check visibility (catches display:none / visibility:hidden ancestors)
      if (el.offsetParent === null && getComputedStyle(el).position !== "fixed") return false;
      return true;
    };

    const getFocusableElements = (): HTMLElement[] => {
      const nodes = dialog.querySelectorAll<HTMLElement>(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      return Array.from(nodes).filter(isFocusable);
    };

    const handleTabKey = (e: KeyboardEvent) => {
      if (e.key !== "Tab") return;

      const focusableElements = getFocusableElements();
      if (focusableElements.length === 0) return;

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      if (e.shiftKey) {
        if (document.activeElement === firstElement) {
          e.preventDefault();
          lastElement?.focus();
        }
      } else {
        if (document.activeElement === lastElement) {
          e.preventDefault();
          firstElement?.focus();
        }
      }
    };

    dialog.addEventListener("keydown", handleTabKey as EventListener);
    getFocusableElements()[0]?.focus();

    return () => {
      dialog.removeEventListener("keydown", handleTabKey as EventListener);
    };
  }, [isOpen]);

  if (!isOpen) return null;

  if (typeof window === "undefined") return null;

  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget && closeOnOverlayClick) {
      handleClose();
    }
  };

  const dialogContent = (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm animate-in fade-in duration-200"
        aria-hidden="true"
      />

      {/* Positioner */}
      <div
        className="fixed inset-0 z-50 flex items-center justify-center p-4"
        onClick={handleOverlayClick}
      >
        <div
          ref={dialogRef}
          className={cn(
            "relative w-full bg-white rounded-2xl shadow-2xl flex flex-col",
            "animate-in zoom-in-95 duration-200",
            sizeStyles[size],
            className
          )}
          role="dialog"
          aria-modal="true"
          aria-labelledby={title ? titleId : undefined}
          aria-describedby={subtitle || description ? descriptionId : undefined}
        >
        {/* Header */}
        {(title || subtitle || showCloseButton) && (
          <div className={cn("shrink-0 flex items-center justify-between p-6", dividers && "border-b border-gray-200", {
            "pb-2": !dividers,
          })}>
            <div className="flex items-center gap-3 flex-1 pr-4">
              {(title || subtitle) && (
                <div className="flex flex-col justify-center min-h-[2.5rem]">
                  {title && (
                    <Typography variant="headline3" as="h2" leading="leading-tight" id={titleId}>
                      {title}
                    </Typography>
                  )}
                  {subtitle && (
                    <Typography
                      variant="body"
                      color="muted"
                      leading="leading-tight"
                      className="mt-0.5"
                      id={descriptionId}
                    >
                      {subtitle}
                    </Typography>
                  )}
                </div>
              )}
            </div>
            {showCloseButton && (
              <Button
                iconOnly
                icon={X}
                variant="ghost"
                size="md"
                onClick={handleClose}
                aria-label="Close dialog"
                className="flex-shrink-0 text-gray-400 hover:text-gray-600 hover:bg-gray-100 border-transparent"
              />
            )}
          </div>
        )}

        {/* Content */}
        <div className={cn("overflow-y-auto max-h-[calc(100vh-16rem)]", {
          "p-6": dividers,
          "px-6": !dividers,
        }, contentClassName)}>
          {description && (
            <Typography
              variant="body"
              color="muted"
              className="mb-4"
              id={!subtitle ? descriptionId : undefined}
            >
              {description}
            </Typography>
          )}
          {children}
        </div>

        {/* Footer */}
        {footer && (
          <div className={cn("shrink-0 flex items-center justify-end gap-3 p-6 rounded-b-2xl", dividers && "border-t border-gray-200")}>
            {footer}
          </div>
        )}
      </div>
      </div>
    </>
  );

  return createPortal(dialogContent, document.body);
}

/**
 * Standard footer for Dialog with cancel and confirm actions.
 *
 * The `variant` prop drives the confirm button color automatically:
 * - `danger` → red confirm button
 * - `warning` → amber confirm button
 * - `success` → green confirm button
 * - `info` / `default` → primary blue confirm button
 *
 * `cancelText` should match the dialog context:
 * - Destructive dialogs: "Cancel"
 * - Warning / unsaved changes: "Keep editing" or "Go back"
 * - Informational: "Close"
 */
export function DialogFooter({
  onCancel,
  onConfirm,
  cancelText = "Cancel",
  confirmText = "Confirm",
  isLoading = false,
  variant = "default",
}: {
  onCancel: () => void;
  onConfirm: () => void;
  cancelText?: string;
  confirmText?: string;
  isLoading?: boolean;
  variant?: "default" | "danger" | "warning" | "success" | "info";
}) {
  const confirmButtonVariant: Record<string, "primary" | "danger" | "warning" | "success"> = {
    default: "primary",
    info: "primary",
    danger: "danger",
    warning: "warning",
    success: "success",
  };

  return (
    <>
      <Button variant="white" onClick={onCancel} disabled={isLoading}>
        {cancelText}
      </Button>
      <Button
        variant={confirmButtonVariant[variant]}
        onClick={onConfirm}
        disabled={isLoading}
      >
        {isLoading ? "Loading..." : confirmText}
      </Button>
    </>
  );
}
